// Whether this browser can play a video the media encoder wrote. The API names
// only the container (`video/mp4`, `video/webm`), never the codec, so the codec
// is assumed to be the encoder's: HEVC + AAC in MP4 by default, VP9 + Opus in
// WebM as an option. A file served as uploaded may be anything else — but such
// a file has no backup rendition, so nothing acts on a wrong guess about it.
const ENCODED_CODECS: Record<string, string> = {
  'video/mp4': 'video/mp4; codecs="hvc1.1.6.L93.B0, mp4a.40.2"',
  'video/webm': 'video/webm; codecs="vp9, opus"',
};

let probe: HTMLVideoElement | null = null;

/**
 * True when the browser says outright that it cannot play an encoded file of
 * this type — `canPlayType` answering "" rather than "maybe"/"probably". Only
 * the encoder's two types are judged; anything else is never ruled out. A
 * "maybe" proves nothing, so the player still has to watch for the failure.
 */
export const cannotPlayEncoded = (contentType?: null | string) => {
  const codecs = contentType ? ENCODED_CODECS[contentType] : undefined;
  if (!codecs || typeof document === 'undefined') return false;
  probe ??= document.createElement('video');
  return probe.canPlayType(codecs) === '';
};

/**
 * A media error that can mean the format or codec is the problem. A decode
 * error always does: the bytes arrived. "Source not supported" also covers any
 * fetch that failed before the first byte — a 401 or a 409 looks the same — so a
 * streamed link has to be asked for its status before it is blamed on the codec.
 * Network and abort errors never are.
 */
export const isUnplayableMediaError = (error: MediaError | null | undefined) =>
  error?.code === MediaError.MEDIA_ERR_DECODE ||
  error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED;

export const isDecodeMediaError = (error: MediaError | null | undefined) =>
  error?.code === MediaError.MEDIA_ERR_DECODE;

/**
 * Metadata read, but no picture: how a Chromium without an HEVC decoder "plays"
 * an HEVC file — the AAC track alone, over a black frame.
 */
export const hasNoPicture = (video: HTMLVideoElement) =>
  video.videoWidth === 0 && video.videoHeight === 0;
